'use client';

import { useState } from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { useShop } from '@/context/ShopContext';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { useProduct, useSubmitReview } from '@/hooks/useProducts';
import { useParams } from 'next/navigation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { RelatedProducts } from '../components/RelatedProducts';
import { ProductReviews } from '../components/ProductReviews';
import { ProductDetails } from '../components/ProductDetails';
import { AddReview } from '../components/AddReview';
import { ProductReview } from '@/types/products';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';
import { ProductDetailSkeleton } from './components/ProductDetailSkeleton';

// Helper function to check if URL is a video
const isVideoUrl = (url: string): boolean => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext)) || lowerUrl.includes('/video/');
};

export default function ProductPageClient() {
    const [quantity, setQuantity] = useState<number>(1);
    const [currentImage, setCurrentImage] = useState<number>(0);
    const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
    const [isAddingToWishlist, setIsAddingToWishlist] = useState<boolean>(false);
    const [isSubmittingReview, setIsSubmittingReview] = useState<boolean>(false);
    const { addToCart, addToWishlist, isInWishlist, removeFromWishlist } = useShop();
    const { language } = useLanguage();
    const t = useTranslation(language);
    
    const params = useParams();
    const id = params.id as string;

    // Submit review
    const { mutateAsync: submitReviewAsync } = useSubmitReview();
    
    // Fetch product from API
    const { data: product, isLoading, isError, refetch } = useProduct(id);

    const router = useRouter();

    if (isLoading) {
        return <ProductDetailSkeleton />;
    }

    if (isError) {
        return (
            <div className="organic min-h-screen flex items-center justify-center">
                <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>{t('product.error')}</p>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="organic min-h-screen flex items-center justify-center">
                <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>{t('product.notFound')}</p>
            </div>
        )
    }

    const handleAddToCart = () => {
        if (!product || !product.available) {
            return;
        }

        try {
            setIsAddingToCart(true);
            addToCart({
                productId: product.productId,
                name: product.name,
                price: product.price,
                quantity: quantity,
                image: product.images[0],
                stock: product.stock, // Add stock information to cart item
            });
        } catch (error) {
            console.error('Error adding to cart:', error);
        } finally {
            setIsAddingToCart(false);
        }
    };
    
    const handleWishlistToggle = () => {
        if (!product) return;
        
        try {
            setIsAddingToWishlist(true);
            
            if (isInWishlist(product.productId)) {
                removeFromWishlist(product.productId);
            } else {
                addToWishlist({
                    productId: product.productId,
                    name: product.name,
                    price: product.price,
                    images: product.images,
                });
            }
        } catch (error) {
            console.error('Error updating wishlist:', error);
        } finally {
            setIsAddingToWishlist(false);
        }
    };

    const handleReviewSubmit = async (review: ProductReview): Promise<{ status: number } | undefined> => {
        if (!product) return undefined;
        
        setIsSubmittingReview(true);
        try {
            const response = await submitReviewAsync(review);
            
            if (response && response.success) {
                // Refresh product data to show the new review
                await refetch();
                setIsSubmittingReview(false);
                return { status: 200 };
            } else {
                setIsSubmittingReview(false);
                return { status: 400 };
            }
        } catch (error) {
            console.error('Failed to submit review:', error);
            setIsSubmittingReview(false);
            return { status: 500 };
        }
    };

    return (
        <main className="organic min-h-screen">
        <div className="max-w-7xl mx-auto" style={{ padding: 'calc(64px + var(--space-6)) clamp(20px, 4vw, 48px) calc(var(--space-8) * 2)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-8)' }}>
                {/* Product Images */}
                <div>
                    <div className="overflow-hidden" style={{ marginBottom: 'var(--space-3)', borderRadius: 'var(--radius-lg)', background: 'var(--color-neutral-100)' }}>
                        {isVideoUrl(product.images[currentImage] || '') ? (
                            <video
                                src={product.images[currentImage]}
                                controls
                                className="w-full h-auto object-cover"
                                style={{ maxHeight: '600px' }}
                            >
                                Your browser does not support the video tag.
                            </video>
                        ) : (
                            <Image
                                src={product.images[currentImage] || '/placeholder-product.jpg'}
                                alt={product.name}
                                width={600}
                                height={800}
                                className="washed w-full h-auto object-cover"
                                priority
                            />
                        )}
                    </div>

                    {product.images && product.images.length > 1 && (
                        <div className="grid grid-cols-4" style={{ gap: 'var(--space-2)' }}>
                            {product.images.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentImage(index)}
                                    className="overflow-hidden"
                                    style={{
                                        borderRadius: 'var(--radius-md)',
                                        border: `2px solid ${currentImage === index ? 'var(--color-accent)' : 'transparent'}`,
                                    }}
                                >
                                    {isVideoUrl(image) ? (
                                        <div className="relative w-full h-24 flex items-center justify-center" style={{ background: 'var(--color-neutral-100)' }}>
                                            <svg className="w-8 h-8" style={{ color: 'var(--color-neutral-500)' }} fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                            </svg>
                                            <span className="absolute bottom-1 right-1 text-xs bg-black/70 text-white px-1 rounded">Video</span>
                                        </div>
                                    ) : (
                                        <Image
                                            src={image || '/placeholder-product.jpg'}
                                            alt={`${product.name} ${index + 1}`}
                                            width={100}
                                            height={100}
                                            className="washed w-full h-24 object-cover"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Product Info */}
                <div>
                    <h1 style={{ margin: '0 0 var(--space-2)', fontSize: 'clamp(26px, 3vw, 34px)' }}>{product.name}</h1>
                    <p style={{ fontFamily: 'var(--font-heading)', fontSize: 24, color: 'var(--color-accent)', margin: '0 0 var(--space-4)' }}>
                        {(product.price / 100).toFixed(2)} RON
                    </p>

                    {/* Rating */}
                    {product.reviewCount && product.reviewCount > 0 ? (
                        <div className="flex items-center gap-2" style={{ marginBottom: 'var(--space-4)' }}>
                            <span style={{ fontSize: 14, color: 'color-mix(in srgb, var(--color-text) 70%, transparent)' }}>
                                ★ {(product.rating ?? 0).toFixed(1)} ({product.reviewCount} {product.reviewCount === 1 ? 'review' : 'reviews'})
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2" style={{ marginBottom: 'var(--space-4)' }}>
                            <span style={{ fontSize: 14, color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}>{t('review.noReviews')}</span>
                        </div>
                    )}

                    <p style={{ color: 'color-mix(in srgb, var(--color-text) 82%, transparent)', lineHeight: 1.6, margin: '0 0 var(--space-6)' }}>
                        {product.description}
                    </p>

                    {/* Flower Details */}
                    {product.flowerDetails && (
                        <div className="card elev-sm" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            {product.flowerDetails.colors && product.flowerDetails.colors.length > 0 && (
                                <div>
                                    <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>🎨 Culoare:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {product.flowerDetails.colors.map((color) => (
                                            <span key={color} className="tag tag-outline capitalize">{color}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {product.flowerDetails.occasions && product.flowerDetails.occasions.length > 0 && (
                                <div>
                                    <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>🎉 Perfect pentru:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {product.flowerDetails.occasions.map((occasion) => (
                                            <span key={occasion} className="tag tag-outline capitalize">{occasion.replace('-', ' ')}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {product.flowerDetails.stemCount && (
                                <p style={{ fontSize: 13.5, margin: 0 }}>
                                    <span style={{ fontWeight: 600 }}>🌹 Numar fire:</span> {product.flowerDetails.stemCount}
                                </p>
                            )}

                            {product.flowerDetails.sameDayDelivery && (
                                <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-accent-2-700)', margin: 0 }}>
                                    🚚 {t('product.sameDayDelivery')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Quantity */}
                    {product.available && (
                        <div style={{ marginBottom: 'var(--space-6)' }}>
                            <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 var(--space-2)' }}>{t('product.quantity')}</p>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center" style={{ border: '1px solid var(--color-divider)', borderRadius: 999, overflow: 'hidden' }}>
                                    <button
                                        onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                                        disabled={quantity <= 1}
                                        className="disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                        style={{ padding: '6px 14px', background: 'none', border: 'none' }}
                                    >
                                        −
                                    </button>
                                    <span style={{ padding: '6px 14px', minWidth: 32, textAlign: 'center' }}>{quantity}</span>
                                    <button
                                        onClick={() => {
                                            const maxStock = product.stock || 20;
                                            setQuantity(prev => Math.min(maxStock, prev + 1));
                                        }}
                                        disabled={product.stock !== undefined && quantity >= product.stock}
                                        className="disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                        style={{ padding: '6px 14px', background: 'none', border: 'none' }}
                                    >
                                        +
                                    </button>
                                </div>
                                {product.stock !== undefined && (
                                    <span style={{ fontSize: 12, color: 'color-mix(in srgb, var(--color-text) 55%, transparent)' }}>
                                        Max: {product.stock}
                                    </span>
                                )}
                            </div>
                            {product.stock !== undefined && quantity >= product.stock && (
                                <p style={{ fontSize: 12, color: 'var(--color-accent-700)', margin: 'var(--space-2) 0 0' }}>
                                    ⚠️ Ai atins cantitatea maximă disponibilă
                                </p>
                            )}
                        </div>
                    )}

                    {/* Stock Availability */}
                    {product.available && product.stock !== undefined && (
                        <div style={{ marginBottom: 'var(--space-6)' }}>
                            {(() => {
                                const stockColor = product.stock > 5 ? 'var(--color-accent-2-700)' : product.stock > 0 ? 'var(--color-accent-700)' : '#b91c1c';
                                const stockLabel = product.stock > 10
                                    ? '✓ În stoc - disponibil imediat'
                                    : product.stock > 5
                                        ? `✓ În stoc (${product.stock} bucăți disponibile)`
                                        : product.stock > 0
                                            ? `⚠️ Doar ${product.stock} ${product.stock === 1 ? 'bucată rămasă' : 'bucăți rămase'} - comandă rapid!`
                                            : '✕ Stoc epuizat';
                                return (
                                    <div className="card elev-sm flex items-center gap-2" style={{ padding: 'var(--space-3) var(--space-4)' }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: stockColor, display: 'inline-block' }} />
                                        <span style={{ fontSize: 13.5, fontWeight: 600, color: stockColor }}>{stockLabel}</span>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Wishlist Button */}
                    <button
                        onClick={handleWishlistToggle}
                        disabled={isAddingToWishlist}
                        className={`btn btn-block btn-secondary ${isInWishlist(product.productId) ? '' : ''}`}
                        style={isInWishlist(product.productId) ? { color: 'var(--color-accent)', borderColor: 'var(--color-accent)' } : undefined}
                    >
                        {isAddingToWishlist ? (
                            'Processing...'
                        ) : isInWishlist(product.productId) ? (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                </svg>
                                {t('product.removeFromWishlist')}
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                {t('product.addToWishlist')}
                            </>
                        )}
                    </button>

                    {/* Add to Cart Button */}
                    <button
                        onClick={handleAddToCart}
                        disabled={isAddingToCart || !product.available}
                        className="btn btn-primary btn-block disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ marginTop: 'var(--space-2)' }}
                    >
                        {isAddingToCart ? (
                            'Adding to Cart...'
                        ) : !product.available ? (
                            t('product.outOfStock')
                        ) : (
                            `${t('product.addToCart')} - ${((product.price * quantity) / 100).toFixed(2)} RON`
                        )}
                    </button>

                    {/* Unavailable Product Message */}
                    {!product.available && (
                        <div className="card" style={{ marginTop: 'var(--space-3)', padding: 'var(--space-4)', background: 'var(--color-accent-100)' }}>
                            <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-accent-800)', margin: '0 0 4px' }}>
                                {t('product.unavailableTitle')}
                            </p>
                            <p style={{ fontSize: 13.5, color: 'var(--color-accent-700)', margin: 0 }}>
                                {t('product.unavailableMessage')}
                            </p>
                            <Link href="/contact" style={{ display: 'inline-block', marginTop: 6, fontSize: 13.5, fontWeight: 600 }}>
                                {t('product.contactUs')} →
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Product Tabs */}
            <div style={{ marginTop: 'calc(var(--space-8) * 1.6)' }}>
                <Tabs defaultValue="details" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 max-w-md" style={{ marginBottom: 'var(--space-6)', background: 'var(--color-surface)', border: '1px solid var(--color-divider)', borderRadius: 999, padding: 4 }}>
                        <TabsTrigger value="details" className="data-[state=active]:bg-[var(--color-accent)] data-[state=active]:text-[var(--color-bg)]" style={{ borderRadius: 999 }}>Detalii</TabsTrigger>
                        <TabsTrigger value="reviews" className="data-[state=active]:bg-[var(--color-accent)] data-[state=active]:text-[var(--color-bg)]" style={{ borderRadius: 999 }}>{t('product.reviews')} ({product.reviewCount})</TabsTrigger>
                        <TabsTrigger value="care" className="data-[state=active]:bg-[var(--color-accent)] data-[state=active]:text-[var(--color-bg)]" style={{ borderRadius: 999 }}>Flower Care</TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="space-y-6">
                        <ProductDetails
                            category={product.category}
                            tags={product.tags}
                            isFeatured={product.isFeatured}
                            isNew={product.isNew}
                            details={product.details || []}
                            rating={product.rating}
                        />

                        <p style={{ color: 'color-mix(in srgb, var(--color-text) 82%, transparent)', lineHeight: 1.6 }}>{product.description}</p>
                    </TabsContent>

                    <TabsContent value="reviews" className="space-y-8">
                        <ProductReviews
                            reviews={product.reviews || []}
                            averageRating={product.rating}
                            reviewCount={product.reviewCount}
                        />
                        { isSubmittingReview ? (
                            <div className="flex flex-col items-center space-y-4">
                                <Spinner className="w-10 h-10" />
                                <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>Submitting your review...</p>
                            </div>
                        ) : (
                            <AddReview
                                productId={product.productId}
                                onReviewSubmit={handleReviewSubmit}
                            />
                        )}
                    </TabsContent>

                    <TabsContent value="care">
                        <h2 style={{ fontSize: 22, margin: '0 0 var(--space-4)' }}>🌱 {t('product.careInstructions')}</h2>

                        {product.flowerDetails?.careInstructions ? (
                            <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
                                {product.flowerDetails.careInstructions.wateringFrequency && (
                                    <div className="card elev-sm" style={{ padding: 'var(--space-4)' }}>
                                        <p style={{ fontWeight: 600, margin: '0 0 6px' }}>💧 Watering</p>
                                        <p style={{ margin: 0, color: 'color-mix(in srgb, var(--color-text) 78%, transparent)' }}>{product.flowerDetails.careInstructions.wateringFrequency}</p>
                                    </div>
                                )}

                                {product.flowerDetails.careInstructions.sunlightRequirement && (
                                    <div className="card elev-sm" style={{ padding: 'var(--space-4)' }}>
                                        <p style={{ fontWeight: 600, margin: '0 0 6px' }}>☀️ Sunlight</p>
                                        <p style={{ margin: 0, color: 'color-mix(in srgb, var(--color-text) 78%, transparent)' }}>{product.flowerDetails.careInstructions.sunlightRequirement}</p>
                                    </div>
                                )}

                                {product.flowerDetails.careInstructions.temperature && (
                                    <div className="card elev-sm" style={{ padding: 'var(--space-4)' }}>
                                        <p style={{ fontWeight: 600, margin: '0 0 6px' }}>🌡️ Temperature</p>
                                        <p style={{ margin: 0, color: 'color-mix(in srgb, var(--color-text) 78%, transparent)' }}>{product.flowerDetails.careInstructions.temperature}</p>
                                    </div>
                                )}

                                {product.flowerDetails.careInstructions.expectedLifespan && (
                                    <div className="card elev-sm" style={{ padding: 'var(--space-4)' }}>
                                        <p style={{ fontWeight: 600, margin: '0 0 6px' }}>⏱️ Expected Lifespan</p>
                                        <p style={{ margin: 0, color: 'color-mix(in srgb, var(--color-text) 78%, transparent)' }}>{product.flowerDetails.careInstructions.expectedLifespan}</p>
                                    </div>
                                )}

                                {product.flowerDetails.careInstructions.specialNotes && (
                                    <div className="card elev-sm" style={{ padding: 'var(--space-4)' }}>
                                        <p style={{ fontWeight: 600, margin: '0 0 6px' }}>📝 Special Notes</p>
                                        <p style={{ margin: 0, color: 'color-mix(in srgb, var(--color-text) 78%, transparent)' }}>{product.flowerDetails.careInstructions.specialNotes}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="card elev-sm" style={{ padding: 'var(--space-6)' }}>
                                <p style={{ fontWeight: 600, margin: '0 0 var(--space-2)' }}>General Flower Care Tips:</p>
                                <ul style={{ margin: 0, paddingLeft: 20, color: 'color-mix(in srgb, var(--color-text) 78%, transparent)', lineHeight: 1.8 }}>
                                    <li>Keep flowers in fresh, clean water</li>
                                    <li>Change water every 2-3 days</li>
                                    <li>Trim stems at a 45° angle</li>
                                    <li>Remove leaves below water line</li>
                                    <li>Keep away from direct sunlight and heat sources</li>
                                    <li>Place in a cool location for longer freshness</li>
                                </ul>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            {/* Related Products */}
            <RelatedProducts currentProductId={product.productId} />

            {/* Back to Shop */}
            <div className="text-center" style={{ marginTop: 'var(--space-8)' }}>
                <button onClick={() => router.push('/shop')} className="btn btn-secondary">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Înapoi la magazin
                </button>
            </div>
        </div>
        </main>
    );
}
